import { Minus, Plus, ShoppingBag, Trash2, X } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { routes } from '@/app/router/route-paths';
import { useStoreInfo } from '@/features/storefront/hooks/useCatalog';
import { cn } from '@/shared/utils/cn';
import { formatPKR } from '@/shared/utils/format';

import { useCart } from '../hooks/useCart';

export function CartDrawer() {
  const { items, subtotal, count, isOpen, closeCart, setQty, removeItem } = useCart();
  const { data: storeInfo } = useStoreInfo();

  // Lock body scroll while the drawer is open (matches the mobile nav pattern).
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const remaining = (storeInfo?.freeShippingThreshold ?? 0) - subtotal;

  return (
    // overflow-hidden: the closed panel sits translated off-screen right — mobile
    // browsers otherwise count that as scrollable page width.
    <div
      className={cn(
        'fixed inset-0 z-[60] overflow-hidden',
        isOpen ? 'pointer-events-auto' : 'pointer-events-none',
      )}
      aria-hidden={!isOpen}
    >
      {/* Scrim */}
      <button
        type="button"
        tabIndex={isOpen ? 0 : -1}
        aria-label="Close cart"
        onClick={closeCart}
        className={cn(
          'absolute inset-0 h-full w-full cursor-default bg-[color:var(--color-ink)]/30 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0',
        )}
      />

      {/* Panel */}
      <aside
        aria-label="Shopping bag"
        className={cn(
          'absolute top-0 right-0 flex h-full w-[90%] max-w-md flex-col bg-white shadow-[var(--shadow-lift)] transition-transform duration-300',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <header className="flex items-center justify-between border-b border-[color:var(--color-line)] px-6 py-5">
          <h2 className="flex items-center gap-2 font-serif text-2xl text-[color:var(--color-ink)]">
            Your Bag
            {count > 0 && (
              <span className="text-sm text-[color:var(--color-muted)]">({count})</span>
            )}
          </h2>
          <button
            type="button"
            onClick={closeCart}
            aria-label="Close cart"
            className="inline-flex size-10 items-center justify-center rounded-full text-[color:var(--color-body)] hover:bg-gold-50"
          >
            <X size={22} strokeWidth={1.75} />
          </button>
        </header>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
            <ShoppingBag size={40} strokeWidth={1.2} className="text-gold-400" />
            <p className="font-serif text-xl text-[color:var(--color-ink)]">Your bag is empty</p>
            <p className="text-sm text-[color:var(--color-muted)]">
              Discover a scent that lingers longer than the evening.
            </p>
            <Link
              to={routes.collection}
              onClick={closeCart}
              className="mt-2 inline-flex rounded-full bg-gold-500 px-7 py-3 text-sm font-semibold text-white transition hover:bg-gold-600"
            >
              Explore the collection
            </Link>
          </div>
        ) : (
          <>
            {/* Free-shipping progress */}
            {remaining > 0 ? (
              <p className="border-b border-[color:var(--color-line)] bg-[color:var(--color-cream)] px-6 py-3 text-center text-xs text-[color:var(--color-body)]">
                Add <span className="font-semibold text-gold-700">{formatPKR(remaining)}</span> more
                for free delivery
              </p>
            ) : (
              <p className="border-b border-[color:var(--color-line)] bg-[color:var(--color-cream)] px-6 py-3 text-center text-xs font-semibold text-gold-700">
                You’ve unlocked free delivery ✨
              </p>
            )}

            <ul className="flex-1 divide-y divide-[color:var(--color-line)] overflow-y-auto px-6">
              {items.map((line) => (
                <li key={line.key} className="flex gap-4 py-5">
                  <div className="size-20 shrink-0 overflow-hidden rounded-xl bg-[color:var(--color-section)]">
                    <img
                      src={line.image}
                      alt={line.name}
                      className="h-full w-full object-contain p-2"
                    />
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-serif text-lg leading-tight text-[color:var(--color-ink)]">
                          {line.name}
                        </p>
                        <p className="mt-0.5 text-xs text-[color:var(--color-muted)]">{line.size}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(line.key)}
                        aria-label={`Remove ${line.name}`}
                        className="text-[color:var(--color-muted)] transition hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-3">
                      <div className="inline-flex items-center rounded-full border border-[color:var(--color-line)]">
                        <button
                          type="button"
                          onClick={() => setQty(line.key, line.qty - 1)}
                          aria-label="Decrease quantity"
                          className="grid size-8 place-items-center rounded-full text-[color:var(--color-body)] hover:text-gold-700"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-7 text-center text-sm font-semibold">{line.qty}</span>
                        <button
                          type="button"
                          onClick={() => setQty(line.key, line.qty + 1)}
                          aria-label="Increase quantity"
                          className="grid size-8 place-items-center rounded-full text-[color:var(--color-body)] hover:text-gold-700"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <span className="font-medium text-[color:var(--color-ink)]">
                        {formatPKR(line.price * line.qty)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <footer className="border-t border-[color:var(--color-line)] px-6 py-5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-[color:var(--color-body)]">Subtotal</span>
                <span className="font-serif text-xl text-[color:var(--color-ink)]">
                  {formatPKR(subtotal)}
                </span>
              </div>
              <p className="mt-1 text-xs text-[color:var(--color-muted)]">
                Delivery & Cash on Delivery calculated at checkout.
              </p>
              <Link
                to={routes.checkout}
                onClick={closeCart}
                className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-gold-500 px-8 py-3.5 text-sm font-semibold text-white shadow-[var(--shadow-soft)] transition hover:bg-gold-600"
              >
                Proceed to checkout
              </Link>
            </footer>
          </>
        )}
      </aside>
    </div>
  );
}
